import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { DollarSign, TrendingUp, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { API } from '../utils/apiConfig';
import './DriverEarnings.css';

const DriverEarnings = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEarnings = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const resp = await fetch(`${API}/rides/driver/earnings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error('Failed');
        const data = await resp.json();
        setEarnings(data);
      } catch (err) {
        console.error('Earnings fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEarnings();
  }, []);

  if (loading) {
    return (
      <div className="earnings-container" data-testid="driver-earnings-page">
        <div className="earnings-loading">
          <div className="spinner" />
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="earnings-container" data-testid="driver-earnings-page">
      <div className="earnings-card">
        <div className="earnings-header">
          <h1 data-testid="earnings-title">
            <TrendingUp size={28} />
            {t('earnings.title', 'Fahrereinnahmen')}
          </h1>
          <p className="earnings-subtitle">{t('earnings.subtitle', 'Deine Einnahmen und Auszahlungen')}</p>
        </div>

        {/* Stats Cards */}
        <div className="earnings-stats" data-testid="earnings-stats">
          <div className="stat-card stat-card--primary" data-testid="total-earned">
            <div className="stat-icon"><DollarSign size={24} /></div>
            <div className="stat-content">
              <div className="stat-value">{earnings?.total_earned?.toFixed(2) || '0.00'} EUR</div>
              <div className="stat-label">{t('earnings.totalEarned', 'Gesamteinnahmen')}</div>
            </div>
          </div>

          <div className="stat-card stat-card--green" data-testid="net-payout">
            <div className="stat-icon"><TrendingUp size={24} /></div>
            <div className="stat-content">
              <div className="stat-value">{earnings?.net_payout?.toFixed(2) || '0.00'} EUR</div>
              <div className="stat-label">{t('earnings.netPayout', 'Netto-Auszahlung')}</div>
            </div>
          </div>

          <div className="stat-card stat-card--blue" data-testid="completed-rides">
            <div className="stat-icon"><CheckCircle size={24} /></div>
            <div className="stat-content">
              <div className="stat-value">{earnings?.completed_rides || 0}</div>
              <div className="stat-label">{t('earnings.completedRides', 'Abgeschlossene Fahrten')}</div>
            </div>
          </div>

          <div className="stat-card" data-testid="active-rides">
            <div className="stat-icon"><Clock size={24} /></div>
            <div className="stat-content">
              <div className="stat-value">{earnings?.active_rides || 0}</div>
              <div className="stat-label">{t('earnings.activeRides', 'Aktive Fahrten')}</div>
            </div>
          </div>
        </div>

        {/* Fees info */}
        <div className="earnings-fees-info">
          <span>{t('earnings.totalFees', 'Plattformgebühren')}: {earnings?.total_fees?.toFixed(2) || '0.00'} EUR</span>
        </div>

        {/* Payout History */}
        <div className="payout-section">
          <h2 data-testid="payout-history-title">
            <Clock size={20} />
            {t('earnings.payoutHistory', 'Auszahlungsverlauf')}
          </h2>

          {earnings?.payouts?.length > 0 ? (
            <div className="payout-list" data-testid="payout-list">
              {earnings.payouts.map((p, i) => (
                <div key={p.id || i} className="payout-item" data-testid={`payout-item-${i}`}>
                  <div className="payout-route">
                    <span className="payout-from">{p.pickup_address || '—'}</span>
                    <ArrowRight size={14} />
                    <span className="payout-to">{p.dropoff_address || '—'}</span>
                  </div>
                  <div className="payout-details">
                    <span className="payout-amount">+{(p.net_amount || 0).toFixed ? p.net_amount.toFixed(2) : p.net_amount} EUR</span>
                    <span className={`payout-status payout-status--${p.status}`}>{p.status}</span>
                    <span className="payout-date">
                      {p.created_at ? new Date(p.created_at).toLocaleDateString('de-DE') : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="payout-empty" data-testid="payout-empty">
              {t('earnings.noPayouts', 'Noch keine Auszahlungen vorhanden.')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverEarnings;
